import Tooltip from '../components/ReportTooltip'
import { Question } from '@phosphor-icons/react'
import { formatMoney, safePercent } from '../../../helpers/utils'
import dayjs from '../../../utils/dayjsConfig'
import { REPORT_HELP, TRANSACTION_COPY } from './reportTextConstants'
import { buildActivityEffectivenessValues } from './routeKpiValues'
import { formatReportRatio as ratioText } from './reportRatio'
import {
  formatPendingSettlementCount,
  formatPendingSettlementLabel,
  renderPendingSettlementLabel
} from './pendingSettlement'

const VISIT_TIMESTAMP_PATTERN =
  /^\d{4}-\d{2}-\d{2}(?:[ T]\d{2}:\d{2}:\d{2}|\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?)/

export function reportVisitTime(value, multiDay) {
  if (value == null || value === '' || value === '-') return '—'
  const text = String(value).trim()
  if (!VISIT_TIMESTAMP_PATTERN.test(text)) return '—'
  const date = dayjs(text)
  if (!date.isValid()) return '—'
  return date.format(multiDay ? 'hh:mm A · DD/MM/YYYY' : 'hh:mm A')
}

const renderVisitTime = (value, multiDay) => {
  const [time, date] = reportVisitTime(value, multiDay).split(' · ')
  return (
    <span className="report-visit-time">
      <span>{time}</span>
      {date && (
        <>
          <br />
          <span>{date}</span>
        </>
      )}
    </span>
  )
}
const money = (value) => (
  <span className="ubiqua-money">{value == null ? '—' : formatMoney(value)}</span>
)
const percentText = (a, b) =>
  a == null || b == null ? '—' : `${safePercent(Number(a), Number(b))}%`
const fraction = (numerator, denominator) => (
  <span className="report-percentage">
    <b>{percentText(numerator, denominator)}</b>
    <small>{ratioText(numerator, denominator)}</small>
  </span>
)

const SHORT_HEADERS = {
  first_visit: 'Primera visita',
  last_visit: 'Última visita',
  visit_effectiveness: 'Efect. visita',
  out_of_route_clients: 'Fuera de ruta',
  geofence_activities: 'Actividad en geocerca',
  delivery_documents: 'Documentos entregados',
  tasks: 'Tareas PDV',
  settlement_status: 'Liquidación',
  pending_settlement_count: 'Pend. liquidar',
  cancelled: 'Rechazadas',
  total_refund: 'Devoluciones',
  total_delivery: 'Entregas',
  total_order: 'Pedidos',
  total_charge: 'Cobros'
}
const SHORT_TRANSACTION_HEADERS = {
  summary: 'Efect. comercial',
  orders: 'Efect. venta',
  charges: 'Efect. cobro',
  deliveries: 'Efect. entrega',
  pos: 'Con tareas PDV'
}

export function reportUserColumns(
  tab = 'summary',
  { multiDay = false, isSingleDayRange = true, onOpenUser } = {}
) {
  const [transactionTitle, transactionHelp] = TRANSACTION_COPY[tab] || TRANSACTION_COPY.summary
  const transactionCounts = (row) => {
    const segment = { orders: 'order', charges: 'charge', deliveries: 'delivery' }[tab]
    if (segment) {
      const values = buildActivityEffectivenessValues(row, segment)
      return [values.attendedInRoute, values.planned]
    }
    return [row.transaction_in_route_clients, row.total_in_route_clients]
  }
  const columns = [
    {
      key: 'user_name',
      label: 'Usuario',
      description: REPORT_HELP.user,
      value: (r) => `${r.user_name || '—'} (${r.user_uid})`,
      render: (r) => (
        <span className="ubiqua-identity-stacked">
          <button
            type="button"
            className="report-user-link ubiqua-identity-stacked__name"
            title={r.user_name}
            onClick={(event) => {
              event.stopPropagation()
              onOpenUser?.(event, r.user_uid)
            }}
          >
            {r.user_name || '—'}
          </button>
          <small className="ubiqua-identity-stacked__code">{r.user_uid}</small>
        </span>
      )
    },
    {
      key: 'first_visit',
      label: 'Hora primera visita',
      description: REPORT_HELP.first,
      value: (r) => reportVisitTime(r.first_visit, multiDay),
      render: (r) => renderVisitTime(r.first_visit, multiDay)
    },
    {
      key: 'last_visit',
      label: 'Hora última visita',
      description: REPORT_HELP.last,
      value: (r) => reportVisitTime(r.last_visit, multiDay),
      render: (r) => renderVisitTime(r.last_visit, multiDay)
    },
    {
      key: 'visit_effectiveness',
      label: 'Efectividad de visita',
      description: REPORT_HELP.effectiveness,
      value: (r) =>
        `${percentText(r.total_visited_in_route_clients, r.total_in_route_clients)} (${ratioText(r.total_visited_in_route_clients, r.total_in_route_clients)})`,
      render: (r) => fraction(r.total_visited_in_route_clients, r.total_in_route_clients)
    },
    {
      key: 'out_of_route_clients',
      label: 'Visitas fuera de ruta',
      description: REPORT_HELP.outsideColumn
    },
    ...(tab === 'deliveries'
      ? [
          {
            key: 'delivery_documents',
            label: 'Documentos entregados',
            description: 'Documentos entregados / Total de documentos por entregar en el periodo.',
            value: (r) =>
              ratioText(r.delivery_documents_closed_count, r.delivery_documents_total_count)
          }
        ]
      : []),
    ...(tab === 'refunds'
      ? []
      : [
          {
            key: 'transaction_effectiveness',
            label: transactionTitle,
            description: transactionHelp,
            value: (r) =>
              `${percentText(...transactionCounts(r))} (${ratioText(...transactionCounts(r))})`,
            render: (r) => fraction(...transactionCounts(r))
          }
        ]),
    {
      key: 'geofence_activities',
      label: 'Actividades dentro de la geocerca',
      description: REPORT_HELP.geofence,
      value: (r) => ratioText(r.geofence_activities_count, r.total_activities_count)
    }
  ]
  if (['summary', 'pos'].includes(tab))
    columns.push({
      key: 'tasks',
      label: 'Tareas en sitio',
      description: REPORT_HELP.tasksColumn,
      value: (r) =>
        ratioText(
          r.pos_task_activity_done_task_codes_count,
          r.pos_task_activity_total_clients_count
        )
    })
  if (['charges', 'deliveries'].includes(tab))
    columns.push(
      {
        key: 'settlement_status',
        label: 'Estado de liquidación',
        description: 'Estado de liquidación del usuario. Disponible para periodos de un solo día.',
        value: (r) => formatPendingSettlementLabel(r.settlement_status, isSingleDayRange),
        render: (r) => renderPendingSettlementLabel(r.settlement_status, isSingleDayRange)
      },
      {
        key: 'pending_settlement_count',
        label:
          tab === 'charges' ? 'Cobros pendientes por liquidar' : 'Entregas pendientes por liquidar',
        description: `Cantidad de ${tab === 'charges' ? 'cobros' : 'entregas'} pendientes por liquidar. Disponible para periodos de un solo día.`,
        value: (r) =>
          formatPendingSettlementCount(
            r[
              tab === 'charges'
                ? 'pending_charges_settlement_count'
                : 'pending_deliveries_settlement_count'
            ],
            isSingleDayRange
          )
      },
      {
        key: 'cancelled',
        align: tab === 'deliveries' ? 'right' : undefined,
        render: tab === 'deliveries' ? (r) => money(r.total_delivery_rejected) : undefined,
        label: tab === 'charges' ? 'Anulados' : 'Total entregas rechazadas',
        description:
          tab === 'charges'
            ? 'Cantidad de cobros anulados en el periodo.'
            : 'Monto total de entregas rechazadas en el periodo.',
        value: (r) =>
          tab === 'charges'
            ? (r.charge_cancelled_count ?? '—')
            : r.total_delivery_rejected == null
              ? '—'
              : formatMoney(r.total_delivery_rejected)
      }
    )
  const amounts =
    tab === 'summary'
      ? ['refund', 'delivery', 'order']
      : {
          orders: ['order'],
          refunds: ['refund'],
          charges: ['charge'],
          deliveries: ['delivery'],
          pos: []
        }[tab] || []
  const labels = {
    refund: 'Total devoluciones',
    delivery: 'Total entregas',
    order: 'Total pedidos',
    charge: 'Total cobros'
  }
  amounts.forEach((kind) =>
    columns.push({
      key: `total_${kind}`,
      label: labels[kind],
      description:
        kind === 'charge'
          ? 'Monto total de cobros del usuario en el periodo.'
          : `Monto total de ${labels[kind].replace('Total ', '')} ${kind === 'order' ? 'registrados' : 'registradas'} en el periodo.`,
      align: 'right',
      render: (r) => money(r[`total_${kind}`]),
      value: (r) => (r[`total_${kind}`] == null ? '—' : formatMoney(r[`total_${kind}`]))
    })
  )
  const widths = {
    user_name: 220,
    first_visit: 120,
    last_visit: 120,
    visit_effectiveness: 110,
    out_of_route_clients: 110,
    transaction_effectiveness: 140,
    delivery_documents: 140,
    geofence_activities: 125,
    tasks: 110,
    pending_settlement_count: 160
  }
  const centered = [
    'visit_effectiveness',
    'out_of_route_clients',
    'transaction_effectiveness',
    'delivery_documents',
    'geofence_activities',
    'tasks'
  ]
  return columns.map((c) => ({
    ...c,
    width: widths[c.key] || 135,
    pdfLabel:
      c.key === 'transaction_effectiveness'
        ? SHORT_TRANSACTION_HEADERS[tab] || SHORT_TRANSACTION_HEADERS.summary
        : SHORT_HEADERS[c.key] || c.label,
    headerClassName: c.align === 'right' ? 'text-end' : undefined,
    align: c.align || (centered.includes(c.key) ? 'center' : 'left'),
    headerRender: c.description
      ? () => (
          <Tooltip title={c.description} arrow describeChild enterTouchDelay={0}>
            <span className="report-table-heading" tabIndex={0} aria-label={c.label}>
              <span>
                {c.key === 'transaction_effectiveness'
                  ? SHORT_TRANSACTION_HEADERS[tab] || SHORT_TRANSACTION_HEADERS.summary
                  : c.key === 'cancelled' && tab === 'charges'
                    ? c.label
                    : SHORT_HEADERS[c.key] || c.label}
              </span>
              <Question size={13} aria-hidden="true" />
            </span>
          </Tooltip>
        )
      : undefined,
    render: c.render || ((row) => (c.value ? c.value(row) : (row[c.key] ?? '—')))
  }))
}
